import type { FC } from 'react';
import { Suspense, useCallback } from 'react';
import { useQuery } from 'urql';

import { ErrorBoundary } from '@packages/shared';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import { FilterDetailsDataDocument } from '@packages/gql/generated/shopping/FilterDetailsDataDocument';
import type { FilterDetailsFragmentFragmentDoc } from '@packages/gql/generated/shopping/FilterDetailsFragmentFragmentDoc';

import type { FilterRangeValue, SelectedFilterValue } from '../../types';
import { FilterDetailsLoader } from '../FilterDetailsLoader/FilterDetailsLoader';
import { SimpleFilterValueGridList } from '../SimpleFilterValueGridList/SimpleFilterValueGridList';
import { BasicFilterValueList } from '../BasicFilterValueList/BasicFilterValueList';
import { FilterRangeSlider } from '../FilterRangeSlider/FilterRangeSlider';

/* GraphQL */ `
  fragment FilterDetailsFragment on SearchFilterBase {
    id
    ...FilterDetailsLoaderFragment
  }
`;

export type FilterDetailsProps = {
  requestCacheKey: string;
  maskedData: FragmentType<typeof FilterDetailsFragmentFragmentDoc>;
  /** Currently selected filter value ids */
  pendingFilterValues: SelectedFilterValue[];
  onToggle?: ({
    filterId,
    valueId,
    isSingleSelection,
  }: {
    filterId: string;
    valueId: string;
    isSingleSelection?: boolean;
  }) => void;
  onSetRange?: ({
    filterId,
    selectedRange,
  }: {
    filterId: string;
    selectedRange?: FilterRangeValue;
  }) => void;
  /** Callback fired when an error occurs */
  onLoadingError?: () => void;
  /** Additional styles which should be applied on the container */
  containerClassName?: string;
};

// TODO: isSeoFilter, isSelected for filter values?

/* GraphQL */ `
  query FilterDetailsData($filterId: ID!, $resultId: ID!) {
    searchFilter(filterId: $filterId, resultId: $resultId) {
      __typename
      ...BasicFilterValueListFragment
      ...FilterRangeSliderFragment
      ... on SearchDefaultFilter {
        displayMode
        ...SimpleFilterValueGridListFragment
      }
    }
  }
`;

/**
 * Determines which control to show for adjusting the details for a specific filter
 * */
const FilterDetailsInternal: FC<Omit<FilterDetailsProps, 'onLoadingError'>> = ({
  requestCacheKey,
  maskedData,
  pendingFilterValues,
  onToggle,
  onSetRange,
  containerClassName,
}) => {
  const filter = unmask<typeof FilterDetailsFragmentFragmentDoc>(maskedData);

  const currentFilter = pendingFilterValues?.find(
    (selectedValue) => selectedValue.filterId === filter.id,
  );

  const variables = {
    filterId: filter.id,
    resultId: requestCacheKey,
  };

  const [{ data, fetching, error }] = useQuery({
    query: FilterDetailsDataDocument,
    variables,
  });

  const handleToggle = useCallback(
    ({ valueId, isSingleSelection }: { valueId: string; isSingleSelection?: boolean }) => {
      onToggle?.({ filterId: filter.id, valueId, isSingleSelection });
    },
    [filter.id, onToggle],
  );

  const handleRangeChange = useCallback(
    (selectedRange: FilterRangeValue | undefined) => {
      onSetRange?.({ filterId: filter.id, selectedRange });
    },
    [filter.id, onSetRange],
  );

  // Suspense should handle that
  if (fetching) return null;

  if (!data || error) {
    throw Error('Filter details could not be loaded', error);
  }

  const details = data.searchFilter;

  switch (details.__typename) {
    case 'SearchDefaultFilter': {
      switch (details.displayMode) {
        case 'GRID':
          return (
            <SimpleFilterValueGridList
              key={filter.id}
              containerClassName={containerClassName}
              maskedData={details}
              selectedIds={currentFilter?.selectedValues ?? []}
              onToggle={handleToggle}
            />
          );
        case 'LIST':
          return (
            <BasicFilterValueList
              key={filter.id}
              containerClassName={containerClassName}
              maskedData={details}
              selectedIds={currentFilter?.selectedValues ?? []}
              onToggle={handleToggle}
            />
          );
        default:
          throw new Error('unsupported display mode:', details.displayMode);
      }
    }
    case 'SearchRangeFilter':
      return (
        <FilterRangeSlider
          key={filter.id}
          containerClassName={containerClassName}
          maskedData={details}
          selectedRange={currentFilter?.selectedRange}
          onChangeRange={handleRangeChange}
          selectedFilterValues={currentFilter?.selectedValues}
          onToggleValue={handleToggle}
        />
      );
    default:
      throw new Error('unsupported filter type:', details);
  }
};

/**
 * A ready to use wrapper with the matching loading state to display.
 * Besides the loading state it handles also the call after an error appears
 * */
export const FilterDetails: FC<FilterDetailsProps> = (props: FilterDetailsProps) => {
  const { maskedData, onLoadingError } = props;

  const data = unmask<typeof FilterDetailsFragmentFragmentDoc>(maskedData);

  return (
    <ErrorBoundary errorCallback={onLoadingError}>
      <Suspense fallback={<FilterDetailsLoader maskedData={data} />}>
        <FilterDetailsInternal {...props} />
      </Suspense>
    </ErrorBoundary>
  );
};

// default export necessary for next/dynamic
// eslint-disable-next-line import-x/no-default-export
export default FilterDetails;
